import dayjs from 'dayjs';
import minMax from "dayjs/plugin/minMax";
import { getOffers } from "../../../app/api/getOffers";
import { BaseAction } from "../../../core/actions/BaseAction";
import { getTranslatedMessage } from '../../../Lib/Utils/i18n';
import stopManager from '../../../Lib/Utils/StopManager';
import { calculateDiscountPercentage, convertISO8601ToDuration, convertISO8601ToMinutes, convertTimeToArabic, extractStops, shouldSharePlaceWithGuardian } from "../../../Lib/Utils/validationUtil";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { SHUTTLE_PRODUCT_CODE, TrainTypeEnum, TripTypeEnum } from '../../../Orchestrator/Types/Enums/globalDefaultEnum';
import { IApiResponse, ILeg, IOffer, IOffersResponse, ITrip, OffersRequest } from "../../../Orchestrator/Types/Index";
import { IGetShuttleOfferActionParams } from '../../../Orchestrator/Types/Interface/ActionParams/UpcomingPastBookingActionParams';
import { TripOfferModel } from "../../../Orchestrator/Types/Types/OffersResponse";
import { PriceInfo, TrainType } from "../../../Orchestrator/Types/Types/TrainTypes";
import { showNotification } from "../../../Services/notificationService";
import { convertOffersToTrains, processOffers } from '../Search/GetSearchResultAction';

dayjs.extend(minMax);

class GetOffersForShuttleAction extends BaseAction<{ result: string, data: any }, IGetShuttleOfferActionParams> {

    processOffers(trips: ITrip[], offers: IOffer[]): TripOfferModel[] {
        const tripOffers: TripOfferModel[] = [];
        const now = new Date();

        for (const trip of trips) {
            const tripStartTime = new Date(trip.startTime);

            // Skip trips that have already started today
            if (tripStartTime < now) {
                continue;
            }

            let shuttleOffer: IOffer | undefined;
            let standardOffer: IOffer | undefined;
            let standardProOffer: IOffer | undefined;
            let premiumOffer: IOffer | undefined;

            for (const offer of offers) {
                const coveredTripId = offer.tripCoverage?.coveredTripId;
                const coveredLegIds = offer.tripCoverage?.coveredLegIds || [];

                // Check if the trip ID matches or any leg ID matches
                const isMatchingTrip = coveredTripId === trip.id;
                const isMatchingLeg = trip.legs.some(leg => coveredLegIds.includes(leg.id));

                if (isMatchingTrip && isMatchingLeg) {
                    switch (offer.products[0].code) {
                        case SHUTTLE_PRODUCT_CODE:
                            shuttleOffer = offer;
                            break;
                        case "SFC":
                            standardOffer = offer;
                            break;
                        case "SFF":
                            standardProOffer = offer;
                            break;
                        case "PFF":
                            premiumOffer = offer;
                            break;
                    }
                }
            }
            if (trip?.legs?.length > 0) {
                trip.legs = trip?.legs?.map((leg: ILeg) => {
                    const legOffers: IOffer[] | undefined = offers.filter(offer => offer?.tripCoverage?.coveredTripId === trip.id && offer.tripCoverage?.coveredLegIds?.includes(leg.id));
                    if (legOffers) {
                        leg.legOffer = [...legOffers];
                    }
                    return leg;
                })
            }

            tripOffers.push({
                trip,
                shuttleOffer,
                standardOffer,
                standardProOffer,
                premiumOffer,
            });
        }

        return tripOffers;
    }

    convertOffersToTrains(offersData: TripOfferModel[], type: string = TrainTypeEnum.outbound): TrainType[] {
        if (!offersData || offersData.length === 0) return [];
        const extractedTrains: TrainType[] = [];
        let idCounter = 1;
        offersData.forEach((offerModel) => {
            const trip: ITrip = offerModel.trip;
            const departureTime = convertTimeToArabic(trip.startTime);
            const arrivalTime = convertTimeToArabic(trip.endTime);
            const duration = convertISO8601ToDuration(trip.duration);
            const durationInMinutes = convertISO8601ToMinutes(trip.duration);
            const stops = extractStops(trip);
            const fromName = stopManager.getStopNameById(trip.origin.stopPlaceRef);
            const toName = stopManager.getStopNameById(trip.destination.stopPlaceRef);
            const trainNumbers = trip.legs[0].timedLeg.service.vehicleNumbers.join(", ");
            const transfers = trip?.transfers || 0;
            const shortDate = trip.startTime || ""
            const priceInfo: PriceInfo = {
                standardProducts: [],
                premiumProducts: [],
                shuttle: { price: 0, isSoldOut: true, discount: undefined, seatsLeft: undefined, offerId: "", originalPrice: undefined, descriptionText: "" },
                standardValue: { price: 0, isSoldOut: true, discount: undefined, seatsLeft: undefined, offerId: "", originalPrice: undefined, descriptionText: "" },
                standardPro: { price: 0, isSoldOut: true, discount: undefined, seatsLeft: undefined, offerId: "", originalPrice: undefined, descriptionText: "" },
                premium: { price: 0, isSoldOut: true, discount: undefined, seatsLeft: undefined, offerId: "", originalPrice: undefined, descriptionText: "" },
            };

            if (offerModel.shuttleOffer) {
                let minimalPrice = offerModel.shuttleOffer.offerSummary.minimalPrice.amount || 0;
                const seatsAvailable = 0;
                const scale = offerModel.shuttleOffer.admissionOfferParts[0].price.scale;
                const basePrice = offerModel.shuttleOffer.admissionOfferParts.reduce((sum, part) => sum + part.price.amount, 0) / ((100 * scale) / scale);
                const originalPrice = offerModel.shuttleOffer.admissionOfferParts.reduce((sum, part) => sum + part.originalPrice.amount, 0) / ((100 * scale) / scale);
                const discountPercentage = calculateDiscountPercentage(originalPrice, basePrice);
                const amount = offerModel.shuttleOffer.admissionOfferParts.reduce((total, part) => total + part.price.amount, 0);
                minimalPrice = amount / ((100 * scale) / scale);
                const descriptionText = offerModel.shuttleOffer.products[0]?.descriptiveTexts[0]?.description || "";
                priceInfo.shuttle = {
                    price: minimalPrice,
                    isSoldOut: !Boolean(offerModel.shuttleOffer.offerId),
                    discount: discountPercentage == "" ? undefined : discountPercentage,
                    seatsLeft: seatsAvailable,
                    offerId: offerModel.shuttleOffer.offerId,
                    originalPrice: originalPrice,
                    descriptionText: descriptionText
                };
            }

            if (offerModel.standardOffer) {
                let minimalPrice = offerModel.standardOffer.offerSummary.minimalPrice.amount || 0;
                const seatsAvailable = offerModel?.standardOffer?.reservationOfferParts[0]?.numericAvailability || 0;
                const scale = offerModel.standardOffer.admissionOfferParts[0].price.scale;
                const basePrice = offerModel.standardOffer.admissionOfferParts.reduce((sum, part) => sum + part.price.amount, 0) / ((100 * scale) / scale);
                const originalPrice = offerModel.standardOffer.admissionOfferParts.reduce((sum, part) => sum + part.originalPrice.amount, 0) / ((100 * scale) / scale);
                const discountPercentage = calculateDiscountPercentage(originalPrice, basePrice);
                const amount = offerModel.standardOffer.admissionOfferParts.reduce((total, part) => total + part.price.amount, 0);
                minimalPrice = amount / ((100 * scale) / scale);
                const descriptionText = offerModel.standardOffer.products[0]?.descriptiveTexts[0]?.description || "";

                priceInfo.standardValue = {
                    price: minimalPrice,
                    isSoldOut: !Boolean(offerModel.standardOffer.offerId),
                    discount: discountPercentage == "" ? undefined : discountPercentage,
                    seatsLeft: seatsAvailable,
                    offerId: offerModel.standardOffer.offerId,
                    basePrice: basePrice,
                    originalPrice: originalPrice,
                    descriptionText: descriptionText
                };
            }

            if (offerModel.standardProOffer) {
                let minimalPrice = offerModel.standardProOffer.offerSummary.minimalPrice.amount || 0;
                const seatsAvailable = offerModel?.standardProOffer?.reservationOfferParts[0]?.numericAvailability || 0;
                const scale = offerModel.standardProOffer.admissionOfferParts[0].price.scale;
                const basePrice = offerModel.standardProOffer.admissionOfferParts.reduce((sum, part) => sum + part.price.amount, 0) / ((100 * scale) / scale);
                const originalPrice = offerModel.standardProOffer.admissionOfferParts.reduce((sum, part) => sum + part.originalPrice.amount, 0) / ((100 * scale) / scale);
                const discountPercentage = calculateDiscountPercentage(originalPrice, basePrice);
                const amount = offerModel.standardProOffer.admissionOfferParts.reduce((total, part) => total + part.price.amount, 0);
                minimalPrice = amount / ((100 * scale) / scale);
                const descriptionText = offerModel.standardProOffer.products[0]?.descriptiveTexts[0]?.description || "";
                priceInfo.standardPro = {
                    price: minimalPrice,
                    isSoldOut: !Boolean(offerModel.standardProOffer.offerId),
                    discount: discountPercentage == "" ? undefined : discountPercentage,
                    seatsLeft: seatsAvailable,
                    offerId: offerModel.standardProOffer.offerId,
                    basePrice: basePrice,
                    originalPrice: originalPrice,
                    descriptionText: descriptionText
                };
            }

            if (offerModel.premiumOffer) {
                let minimalPrice = offerModel.premiumOffer.offerSummary.minimalPrice.amount || 0;
                const seatsAvailable = offerModel?.premiumOffer?.reservationOfferParts[0]?.numericAvailability || 0;
                const scale = offerModel.premiumOffer.admissionOfferParts[0].price.scale;
                const basePrice = offerModel.premiumOffer.admissionOfferParts.reduce((sum, part) => sum + part.price.amount, 0) / ((100 * scale) / scale);
                const originalPrice = offerModel.premiumOffer.admissionOfferParts.reduce((sum, part) => sum + part.originalPrice.amount, 0) / ((100 * scale) / scale);
                const discountPercentage = calculateDiscountPercentage(originalPrice, basePrice);
                const amount = offerModel.premiumOffer.admissionOfferParts.reduce((total, part) => total + part.price.amount, 0);
                minimalPrice = amount / ((100 * scale) / scale);
                const descriptionText = offerModel.premiumOffer.products[0]?.descriptiveTexts[0]?.description || "";
                priceInfo.premium = {
                    price: minimalPrice,
                    isSoldOut: !Boolean(offerModel.premiumOffer.offerId),
                    discount: discountPercentage == "" ? undefined : discountPercentage,
                    seatsLeft: seatsAvailable,
                    offerId: offerModel.premiumOffer.offerId,
                    basePrice: basePrice,
                    originalPrice: originalPrice,
                    descriptionText: descriptionText
                };
            }

            const train: TrainType = {
                id: idCounter++,
                departureTime,
                arrivalTime,
                duration,
                durationInMinutes,
                isSoldOut: priceInfo.standardValue.isSoldOut && priceInfo.standardPro.isSoldOut && priceInfo.premium.isSoldOut,
                stops,
                trainNumbers,
                priceInfo,
                type: type,
                selectedPriceClass: null,
                transfers,
                fromName,
                toName,
                shortDate,
                legs: trip.legs
            };

            extractedTrains.push(train);
        });
        return extractedTrains;
    }

    getStopDuration(nextLeg: any, endStop: any): string {
        if (!nextLeg?.timedLeg?.start?.serviceDeparture?.timetabledTime ||
            !endStop?.serviceArrival?.timetabledTime) {
            return "";
        }

        const nextDepartureTime = new Date(nextLeg.timedLeg.start.serviceDeparture.timetabledTime);
        const currentArrivalTime = new Date(endStop.serviceArrival.timetabledTime);

        const durationInMinutes = Math.floor((nextDepartureTime.getTime() - currentArrivalTime.getTime()) / (1000 * 60));
        return `${durationInMinutes}min`;
    }

    private storedPassengerRefs: { [key: string]: string } = {};

    override async run(): Promise<{ result: string, data: any }> {
        try {
            const { from, to, passenger, trainType, arrivalTime, departureTime } = this.params!;
            const newOfferRequest: OffersRequest = {
                tripSearchCriteria: {
                    departureTime: departureTime,
                    origin: {
                        stopPlaceRef: from.id,
                        objectType: "StopPlaceRef",
                    },
                    destination: {
                        stopPlaceRef: to.id,
                        objectType: "StopPlaceRef",
                    },
                    // Always outbound, even on the inbound train leg: this
                    // describes the SHUTTLE's own hop, not the train's
                    // direction, and an inbound-stamped shuttle offer is priced
                    // fine but rejected at POST /booked-offers with
                    // INVALID_OFFER_SELECTION. Same reason as
                    // shuttleOffersController — see the note there.
                    direction: TripTypeEnum.OUT_BOUND
                    // returnSearchParameters: arrivalTime ?
                    //     {
                    //         ...(trainType === TrainTypeEnum.outbound ?
                    //             { inwardReturnDate: arrivalTime } :
                    //             { outwardTripDate: arrivalTime }
                    //         )
                    //     } : null
                },
                offerSearchCriteria: {
                    currency: "AED",
                },
                anonymousPassengerSpecifications: []

            };

            if (passenger) {
                newOfferRequest.anonymousPassengerSpecifications = passenger.map((passenger: any) => ({
                    externalRef: passenger.externalRef,
                    age: passenger.age,
                    type: 'PERSON',
                    cards: passenger.cards,
                    prmNeeds: passenger.prmNeeds,
                }));

            }


            newOfferRequest.anonymousPassengerSpecifications = newOfferRequest.anonymousPassengerSpecifications.map((passenger: any) => {
                if (shouldSharePlaceWithGuardian(passenger)) {
                    return { ...passenger, sharePlaceWithGuardian: true };
                }
                return passenger;
            });

            updateRecoilState<OffersRequest>('offersRequestAtom', newOfferRequest);

            // Clear the trainAtom before fetching new data
            updateRecoilState("trainAtom", []);
            updateRecoilState("filterTrainsAtom", [])

            //updateRecoilState<IPassengerSpecification[]>("passengerSpecificationsTypesAtom", passengerSpecificationsTypes);

            const response: IApiResponse<IOffersResponse> = await getOffers(newOfferRequest);
            let convertedTrainsData;
            // console.log("newOfferRequest=>", newOfferRequest)
            if (response.status === "ok" && response.answer) {
                const offersData: IOffersResponse = response.answer;

                if (offersData.offers.length > 0) {
                    //Updates the offerAtom                    
                    updateRecoilState("offerAtom", offersData.offers);
                    updateRecoilState("tripOfferAtom", offersData.trips);

                    const tripOffers = processOffers(offersData.trips, offersData.offers);

                    //console.log("tripOffers=>", tripOffers)

                    // const convertedTrains = this.convertOffersToTrains(tripOffers, trainType);
                    const convertedTrains = convertOffersToTrains(tripOffers, trainType);

                    updateRecoilState("trainAtom", convertedTrains);
                    convertedTrainsData = convertedTrains

                } else {
                    showNotification('warning', getTranslatedMessage("noOffers"), getTranslatedMessage("selectDifferentSearch"));
                }
            } else {

            }
            //return "true"
            let responseData = {
                result: "true",
                data: convertedTrainsData
            }
            return responseData;
        } catch (error: any) {
            console.error("Error fetching offers:", error);
            //return "false"
            let responseData = {
                result: "false",
                data: []
            }
            return responseData;
        }
    }
}

export const getOffersForShuttleAction = new GetOffersForShuttleAction()

